/**
 * Un grupo del menú que quedó con un solo hijo visible deja de ser un grupo.
 *
 * Cuando a un cliente se le ocultan módulos por permisos, los grupos se vacían
 * de a poco. "Commercial Intelligence" tiene 10 entradas; a un agente de carga
 * se le deja una sola, "Clientes". El resultado era un encabezado que promete
 * un módulo entero, hay que hacer clic para desplegarlo, y adentro hay una
 * línea. El nombre del grupo mentía sobre lo que había abajo.
 *
 * La solución es plana: si queda un hijo, el grupo *es* ese hijo. Se muestra
 * "Clientes" directamente, sin desplegar.
 *
 * Es puro a propósito — el filtrado del menú por permisos es la clase de lógica
 * que se rompe en silencio, y probarla dibujando el sidebar entero es caro.
 */

interface FlattenableChild {
  label: string;
  href: string;
  icon: unknown;
  badge?: number;
  badgeTone?: 'neutral' | 'warn';
  description?: string;
  menuKey?: string;
  children?: FlattenableChild[];
}

interface FlattenableGroup {
  id: string;
  label: string;
  icon: unknown;
  href?: string;
  badge?: number;
  description?: string;
  menuKey?: string;
  sectionLabel?: string;
}

/**
 * Devuelve el grupo y los hijos que hay que dibujar.
 *
 * Aplana solo cuando es seguro: un hijo, con destino propio, y que no sea a su
 * vez un sub-grupo. Un sub-grupo con hijos propios (como "Tarifario", que abre
 * cinco pantallas) se deja intacto: aplanarlo escondería sus hijos.
 *
 * `sectionLabel` se conserva. Es el encabezado de la sección, no del grupo, y
 * puede haber otros grupos debajo que pertenezcan a ella.
 */
export function flattenSingleChild<G extends FlattenableGroup, C extends FlattenableChild>(
  group: G,
  children: C[] | undefined
  // `& FlattenableGroup` porque al aplanar el grupo gana `href` aunque el tipo
  // de entrada no lo declarara.
): { group: G & FlattenableGroup; children: C[] | undefined } {
  if (!children || children.length !== 1) return { group, children };

  const only = children[0];

  // Un sub-grupo expandible: sus hijos se perderían al aplanar.
  if (only.children && only.children.length > 0) return { group, children };

  // Sin destino no hay a dónde ir; mejor dejar el grupo como estaba.
  if (!only.href) return { group, children };

  return {
    group: {
      ...group,
      label: only.label,
      href: only.href,
      // El ícono y la descripción pasan a ser los del hijo: la entrada ahora
      // lleva su nombre, así que tiene que llevar también su cara.
      icon: only.icon,
      description: only.description ?? group.description,
      badge: only.badge,
      // El módulo también es el del hijo: de él se lee el candado «Bloqueado».
      // Sin esto, una entrada bloqueada que quedaba sola perdía el candado
      // (P3-D10: Estrategias de Mercado sin Precios Dinámicos).
      menuKey: only.menuKey ?? group.menuKey,
    },
    children: undefined,
  };
}

// ── Encabezados de sección ───────────────────────────────────────

/**
 * Reparte los encabezados de sección entre los grupos que quedaron visibles.
 *
 * En el código la sección la declara el primer grupo que la abre, y los que
 * siguen sin declarar ninguna le pertenecen. Eso se rompe apenas los permisos
 * ocultan justo al que la abría: la sección queda descabezada y sus grupos
 * caen bajo el encabezado anterior. En el menú de un agente de carga,
 * "Administración" y "Ayuda" aparecían debajo de "TRADE INTELLIGENCE", con el
 * que no tienen nada que ver — Super Admin, que abre "Sistema", es solo para
 * el administrador de la plataforma.
 *
 * Acá el encabezado pasa al primer grupo visible de su sección. Un grupo que
 * declara la suya propia siempre la conserva.
 */
export function resolveSectionLabels<T extends { sectionLabel?: string }>(
  groups: T[],
  isVisible: (group: T) => boolean
): { group: T; sectionLabel?: string }[] {
  const resultado: { group: T; sectionLabel?: string }[] = [];
  let pendiente: string | undefined;

  for (const group of groups) {
    // Abrir una sección reemplaza a la anterior, esté o no dibujada: si la de
    // arriba se quedó sin ningún grupo visible, deja de existir.
    if (group.sectionLabel) pendiente = group.sectionLabel;
    if (!isVisible(group)) continue;

    resultado.push({ group, sectionLabel: pendiente });
    pendiente = undefined;
  }

  return resultado;
}

interface SectionCandidate {
  /** Si está, este grupo abre una sección; los que siguen sin él pertenecen a ella. */
  sectionLabel?: string;
  /** El texto que muestra el grupo, ya aplanado si correspondía. */
  label: string;
  /** Si se despliega. Un grupo sin hijos es un enlace suelto. */
  hasChildren: boolean;
}

/**
 * ¿Qué encabezados de sección vale la pena dibujar?
 *
 * Un encabezado sirve para agrupar. Cuando abajo queda una sola entrada deja de
 * agrupar y pasa a ser ruido —o peor, promete algo que no está: a Delicia le
 * quedaba "COMMERCIAL INTELLIGENCE" sobre la palabra "Clientes"—. Lo mismo con
 * "TRADE INTELLIGENCE / Trade Intelligence", que dice dos veces lo mismo.
 *
 * Se dibuja si agrupa a más de un grupo visible. Si está solo, se dibuja
 * únicamente cuando aporta algo que la entrada no dice: un nombre distinto,
 * sobre algo que además se despliega.
 *
 * Recibe los grupos **ya filtrados por permisos y ya aplanados**: la decisión
 * depende de lo que se ve, no de lo que existe en el código.
 */
export function computeSectionHeaders(groups: SectionCandidate[]): boolean[] {
  return groups.map((g, i) => {
    if (!g.sectionLabel) return false;

    // ¿Cuántos grupos más caen bajo este encabezado? Los siguientes le
    // pertenecen hasta que aparece otro que abre su propia sección.
    let acompanantes = 0;
    for (let j = i + 1; j < groups.length && !groups[j].sectionLabel; j++) acompanantes++;

    if (acompanantes > 0) return true;

    return g.hasChildren && g.sectionLabel !== g.label;
  });
}
